// App.tsx - A Dinâmica no shell da WiniEduca (entrega 1.6).
//
// Entrada pelo login único do hub (a lógica de antes, com o endereço do hub por variável, D46), as opções
// do pedido, as rotas e a medição do GA4 único da plataforma (entrega 1.10).

import { Fragment, Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { Button, ButtonLink, EmptyState, IconArrowRight, IconRotateCcw, IconUser, SHELL_TEXT, Skeleton, iniciarMedicao, usePaginaVista } from '@winieduca/ui';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SSO_ME_URL, hubUrl } from './config/hub';
import { auth, cabecalhoAppCheck } from './services/firebase';
import { obterOpcoes, type OpcoesDisponiveis } from './services/api';
import { Moldura } from './components/Moldura';
import { acoes, entrada, pagina } from './content/textos';
import HomePage from './pages/HomePage';
import './telas/telas.css';

// Pré-visualização das telas com dados de exemplo: só no servidor de desenvolvimento, fora do build.
const PreviaTelas = import.meta.env.DEV ? lazy(() => import('./pages/dev/PreviaTelas')) : null;


/** O `uid` do token do login único (JWT do hub). Só para comparar: quem confere a assinatura é o Firebase. */
function uidDoToken(token: string): string | null {
  try {
    const meio = token.split('.')[1] ?? '';
    const uid = (JSON.parse(atob(meio.replace(/-/g, '+').replace(/_/g, '/'))) as { uid?: unknown }).uid;
    return typeof uid === 'string' ? uid : null;
  } catch {
    return null;
  }
}

/** Entrada pelo hub (login único). Sem sessão no hub, o caminho para entrar; com ela, a Dinâmica. */
function AuthGuard({ children }: { children: ReactNode }) {
  const { currentUser, loading } = useAuth();
  const [status, setStatus] = useState<'idle' | 'exchanging' | 'success' | 'failed'>('idle');
  const [tentativa, setTentativa] = useState(0);
  // Uma tentativa por ciclo, sem pôr o status nas dependências (isso cancelaria a troca no meio).
  const tentou = useRef(false);
  // A sessão desta aba já foi conferida com o hub (M9): até lá, nada da conta aparece.
  const [conferida, setConferida] = useState(false);
  const conferidaRef = useRef(false);
  const appId = import.meta.env.VITE_APP_ID || 'dinamica';

  useEffect(() => {
    if (loading || currentUser || tentou.current) return;
    tentou.current = true;
    let montado = true;
    void (async () => {
      setStatus('exchanging');
      try {
        const r = await fetch(SSO_ME_URL, { method: 'GET', credentials: 'include', headers: await cabecalhoAppCheck() });
        if (!r.ok) throw new Error('sso_session_missing');
        const { customToken } = (await r.json()) as { customToken: string };
        // O token acabou de vir do hub: a sessão nova já nasce conferida.
        conferidaRef.current = true;
        await signInWithCustomToken(auth, customToken);
        setConferida(true);
        if (montado) setStatus('success');
      } catch (err) {
        if (montado) setStatus('failed');
      }
    })();
    return () => {
      montado = false;
    };
  }, [loading, currentUser, tentativa, appId]);

  // Sessão guardada de outra visita: antes de mostrar a Dinâmica, confere com o hub. Saiu no hub, a sessão
  // daqui cai junto; outra pessoa entrou no hub (computador da escola), a Dinâmica troca para ela (M9).
  // Erro de rede mantém a sessão.
  useEffect(() => {
    if (loading || !currentUser || conferidaRef.current) return;
    void (async () => {
      try {
        const r = await fetch(SSO_ME_URL, { method: 'GET', credentials: 'include', headers: await cabecalhoAppCheck() });
        if (!r.ok) {
          await auth.signOut();
          return;
        }
        const { customToken } = (await r.json()) as { customToken: string };
        if (uidDoToken(customToken) !== currentUser.uid) {
          conferidaRef.current = true;
          // Outra pessoa: entra com ela; se não der, sai. Nunca segue como a anterior.
          await signInWithCustomToken(auth, customToken).catch(() => auth.signOut());
        }
      } catch {
        // sem rede: segue com a sessão guardada
      } finally {
        // Sempre, mesmo se a troca de conta já rodou este efeito de novo: senão a tela fica em "Carregando".
        conferidaRef.current = true;
        setConferida(true);
      }
    })();
  }, [currentUser, loading]);

  // A chave pelo uid recomeça a tela inteira quando a conta muda: nada da anterior fica na memória.
  if (currentUser && conferida) return <Fragment key={currentUser.uid}>{children}</Fragment>;

  if (status === 'failed') {
    return (
      <div className="din-pagina din-entrada">
        <EmptyState
          icon={IconUser}
          title={entrada.titulo}
          action={
            <div className="din-entrada__acoes">
              <ButtonLink variant="primary" icon={IconArrowRight} href={hubUrl('/login')}>
                {acoes.entrar}
              </ButtonLink>
              <Button
                icon={IconRotateCcw}
                onClick={() => {
                  tentou.current = false;
                  setStatus('idle');
                  setTentativa((n) => n + 1);
                }}
              >
                {entrada.tentar}
              </Button>
            </div>
          }
        >
          {entrada.texto}
        </EmptyState>
      </div>
    );
  }

  return <Carregando />;
}

function Carregando() {
  return (
    <div className="din-pagina">
      <p className="we-visually-hidden" role="status">
        {SHELL_TEXT.carregando}
      </p>
      <Skeleton lines={4} className="din-carregando" />
    </div>
  );
}

/** As opções do pedido vêm da API da Dinâmica; `null` se falharem (a tela oferece tentar de novo). */
function ComOpcoes({ children }: { children: ReactNode }) {
  const [opcoes, setOpcoes] = useState<OpcoesDisponiveis | null | 'carregando'>('carregando');
  const carregar = useCallback(() => {
    setOpcoes('carregando');
    obterOpcoes()
      .then(setOpcoes)
      .catch(() => setOpcoes(null));
  }, []);
  useEffect(() => carregar(), [carregar]);
  if (opcoes === 'carregando') return <Carregando />;
  return <AppProvider value={{ opcoes, recarregarOpcoes: carregar }}>{children}</AppProvider>;
}

/**
 * GA4 único da plataforma (entrega 1.10; ADR-002 §2.5; `09` §3): o fluxo `cerebro-pedagogico` (pelo `.env`),
 * `app_id` `dinamica` e um `page_view` por navegação, com o módulo do `@winieduca/ui`. A Dinâmica é área
 * logada: vale o aceite do cadastro, sem aviso de cookies (D42). Saíram o Firebase Analytics avulso e o Clarity.
 */
function useMedicao() {
  const location = useLocation();
  const [medicao] = useState(() =>
    iniciarMedicao({ id: import.meta.env.VITE_GA_MEASUREMENT_ID, app: 'dinamica', consentimento: 'granted' })
  );
  usePaginaVista(medicao, location.key, location.pathname + location.search);
}

/** A ajuda é uma só, no hub (D129). */
function AjudaNoHub() {
  useEffect(() => {
    window.location.replace(hubUrl('/ajuda'));
  }, []);
  return <Carregando />;
}

function Dinamica() {
  useMedicao();
  useEffect(() => {
    document.title = pagina.aba;
  }, []);
  return (
    <Moldura>
      <AuthGuard>
        <ComOpcoes>
          <HomePage />
        </ComOpcoes>
      </AuthGuard>
    </Moldura>
  );
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<Dinamica />} />
        <Route path="/ajuda/*" element={<AjudaNoHub />} />
        {PreviaTelas && (
          <Route
            path="/dev/telas"
            element={
              <Suspense fallback={null}>
                <PreviaTelas />
              </Suspense>
            }
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
